import { motion } from "framer-motion";
import { Award, Ticket, Truck, Users } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export default function FoundingBeta() {
  return (
    <section
      id="beta"
      data-testid="founding-beta-section"
      className="relative pt-24 md:pt-32 pb-16"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <motion.span {...fadeUp} transition={{ duration: 0.6 }} className="kicker">
          Founding Parents · Hyderabad
        </motion.span>

        <motion.h2
          {...fadeUp}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 font-editorial text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl"
        >
          Be one of Mumzo's <br className="hidden md:block" />
          <span className="italic text-pinkDeep">Founding Parents.</span>
        </motion.h2>

        <motion.p
          {...fadeUp}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-foreground/70 leading-relaxed"
        >
          We're starting small with a limited group of parents in Hyderabad.
          As one of our first customers, you get more than early access.
        </motion.p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-6 md:grid-rows-2 gap-4 md:gap-5">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            data-testid="beta-card-badge"
            className="md:col-span-4 md:row-span-2 relative overflow-hidden rounded-3xl bg-pinkDeep text-white p-8 md:p-10 flex flex-col justify-between min-h-[280px]"
          >
            <div
              aria-hidden
              className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-2xl"
            />
            <div className="relative w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center">
              <Award className="w-5 h-5 text-white" aria-hidden />
            </div>
            <div className="relative mt-8">
              <div className="font-editorial text-5xl md:text-6xl leading-none">
                Founding Parent Badge
              </div>
              <p className="mt-4 text-sm md:text-base text-white/80 max-w-sm leading-relaxed">
                A badge on your account, forever. First access to new
                Products and everything we build next.
              </p>
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.22 }}
            data-testid="beta-card-coupon"
            className="md:col-span-2 rounded-3xl bg-white border border-border/70 p-7 flex flex-col justify-between"
          >
            <div className="w-11 h-11 rounded-2xl bg-blush flex items-center justify-center">
              <Ticket className="w-5 h-5 text-pinkDeep" aria-hidden />
            </div>
            <div className="mt-6">
              <div className="font-editorial text-2xl md:text-3xl leading-tight">
                ₹150 coupons
              </div>
              <p className="mt-2 text-sm text-foreground/65 leading-relaxed">
                Dropped into your account the moment you join.
              </p>
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.29 }}
            data-testid="beta-card-delivery"
            className="md:col-span-2 rounded-3xl bg-pinkSoft border border-rose/40 p-7 flex flex-col justify-between"
          >
            <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center">
              <Truck className="w-5 h-5 text-pinkDeep" aria-hidden />
            </div>
            <div className="mt-6">
              <div className="font-editorial text-2xl md:text-3xl leading-tight">
                Free delivery,
                <br />
                first month.
              </div>
              <p className="mt-2 text-sm text-foreground/65 leading-relaxed">
                Every order, zero delivery fee, for your first 30 days.
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.36 }}
          data-testid="beta-card-whatsapp"
          className="mt-4 md:mt-5 rounded-3xl bg-white border border-border/70 p-7 flex items-start gap-5"
        >
          <div className="shrink-0 w-11 h-11 rounded-2xl bg-blush flex items-center justify-center">
            <Users className="w-5 h-5 text-pinkDeep" aria-hidden />
          </div>
          <div>
            <div className="font-editorial text-2xl md:text-3xl leading-tight">
              Private feedback group
            </div>
            <p className="mt-2 text-sm md:text-base text-foreground/65 leading-relaxed">
              Share feedback and request new Products directly, we're
              building this with you, not just for you.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
